<template>
    <EcrPickerModalVue ref="ecrPartPicker" v-model="availableParts" topLevel="Cost Studies"
        @close="onModalPickerClose" />
    <header>
        <section class="bottom" style="width:100%">
            <div class="bottom-title">
                <p>{{ ReportName }}</p>
            </div>

            <div style="margin-right: 20px;">
                <DropDownListComponent :width="100" :dataSource='currencies' :fields="{ text: 'value', value: 'key' }"
                    v-model:value="currency" />

            </div>

            <nav>
                <router-link to="/compare"><i class="fas fa-caret-left"></i> Back</router-link>
                <a href="#" v-on:click="save"><i class="fas fa-file-excel"></i> Export</a>
                <a href="#" v-show="!isBookmarked" v-on:click="createBookmark"><i class="fas fa-bookmark"></i>
                    Bookmark</a>
                <a href="#" v-show="isBookmarked" v-on:click="removeBookmark"><i class="far fa-bookmark"></i> Remove
                    Bookmark</a>
                <a href="#" @click="launchPartPicker"><i class="fas fa-plus"></i> Add Parts</a>
            </nav>

            <div v-if="tabs[focusedTab]?.PartDataSets.length >= 2" class="additional-controls"
                style="justify-self: flex-end;">
                <div style="display:flex">
                    <label for="toggleHidden" style="margin-right: 10px">Toggle Hidden Columns:</label>
                    <ejs-switch id="toggleHidden" @change="onToggleHidden" />
                </div>
            </div>
        </section>
    </header>

    <div>
        <div class='row'>
            <CostDataHeaderDetail title="BASE MATERIAL COST DATA" :partOptions="partOptions" :slotIndex=0
                :tabIndex="focusedTab" :partId="tabs[focusedTab]?.PartDataSets[0]?.PartId" @yearChange="onYearChanged"
                @partChange="onPartChanged" />

            <CostDataHeaderDetail title="COMPARE-TO MATERIAL COST DATA" :partOptions="partOptions" :slotIndex=1
                :tabIndex="focusedTab" :partId="tabs[focusedTab]?.PartDataSets[1]?.PartId" @yearChange="onYearChanged"
                @partChange="onPartChanged" />
        </div>
    </div>
    <ejs-spreadsheet id="spreadsheet" ref="spreadsheet" :allowEditing="true" :allowCellFormatting="true"
        :allowChart="false" :allowConditionalFormat="true" :allowDataValidation="false" :allowDelete="true"
        :allowFiltering="false" :allowFindAndReplace="false" :allowFreezePane="false" :allowHyperlink="false"
        :allowImage="false" :allowInsert="true" :allowMerge="false" :allowNumberFormatting="true" :allowOpen="false"
        :allowResizing="true" :allowSave="true" :allowWrap="true" :allowScrolling="true" :allowSorting="true"
        :allowUndoRedo="false" :showFormulaBar="true" :showRibbon="false" :showSheetTab="true" :height="height"
        :cellEdit="cellEdit" :scrollSettings="scrollSettings" @select="onSelect"
        @contextMenuBeforeOpen="onContextMenuBeforeOpen" :activeSheetIndex="focusedTab" @actionBegin="onActionBegin"
        @actionComplete="onActionComplete">
    </ejs-spreadsheet>
    <div id="exportpane" ref="exportpane" style="display:none"></div>
</template>

<script>
//import {  SpreadsheetComponent, RangesDirective, RangeDirective, SheetsDirective, SheetDirective } from "@syncfusion/ej2-vue-spreadsheet";
import CostDataHeaderDetail from "./subcomponents/CostDataHeaderDetail.vue";
import { DropDownListComponent } from "@syncfusion/ej2-vue-dropdowns";
import { SpreadsheetComponent } from "@syncfusion/ej2-vue-spreadsheet";
export default {
    components: {
        "ejs-spreadsheet": SpreadsheetComponent,
        CostDataHeaderDetail,
        DropDownListComponent
    }
};
</script>

<script setup>
// https://localhost:44324/walk/component?part=3035016001%7C5e300d14-bead-48bb-9c29-5a803988dd65%7CA%7C38267560&part=3035016001%7C7e0a45fe-ee61-4495-8d00-fbf2865e0ef7%7CA%7C38267560
// https://localhost:44324/walk/component?part=3035016001%7C5e300d14-bead-48bb-9c29-5a803988dd65%7CA%7C38267560&part=3035016001%7C7e0a45fe-ee61-4495-8d00-fbf2865e0ef7%7CA%7C38267560&part=3035906001%7C2d3feb96-e378-44e4-a64f-196a64484b1d%7CA%7C38318678&part=3034479001%7C167c405c-fe53-4a5e-86b9-131bd32e8a5a%7CA%7C38318670

import { ref, onMounted, onUnmounted, defineProps, watch, reactive, defineEmits } from "vue";
import { useRoute } from 'vue-router'
import { DialogUtility } from '@syncfusion/ej2-popups';
import ComponentWalkModel, { ComponentWalkPartModel } from "../models/ComponentWalkModel";
import Bookmark from "../modules/Bookmark";
import { Spreadsheet } from '@syncfusion/ej2-spreadsheet';
import { SwitchComponent as EjsSwitch } from "@syncfusion/ej2-vue-buttons";
import EcrPickerModalVue from './subcomponents/EcrPickerModal.vue';
import { enableRipple } from '@syncfusion/ej2-base';

enableRipple(true);

// H1: Component Properties
const props = defineProps({
    part: Array
});

// H1: Emits
const emit = defineEmits([Bookmark.EventName]);

// H1: Data
const ReportName = ref('Component Walk');
const spreadsheet = ref({});
const currencies = ref(['USD']);
const currency = ref('USD');
const height = ref(800);
const tabLoadingTimer = ref(2500);
const tabs = reactive([]);
const dialog = ref(null);
const route = useRoute();
const isBookmarked = ref(Bookmark.IsBookmarked(route.fullPath));
const scrollSettings = ref({ enableVirtualization: false });
const focusedTab = ref(0);
const partOptions = ref([]);
const exportpane = ref({});
const ecrPartPicker = ref({});
const availableParts = ref([]);
const isExpanded = ref(false);

let rowPendingMove = null;
let selectedRange = "";


// H1: Mount/UnMount
onMounted(() => {
    window.addEventListener('resize', setSpreadsheetHeight);
    setSpreadsheetHeight();

    if (props.part === undefined) {
        ecrPartPicker.value.openModal();
        return;
    }
    if (Array.from(props.part).length > 0) {
        parsePartOptions();
    }
});

onUnmounted(() => {
    window.removeEventListener('resize', setSpreadsheetHeight)
});

// H1: Wachers
watch(focusedTab, (current) => {

    if (tabs[current] !== undefined && tabs[current].Currency !== currency.value) {
        tabs[current].Currency = currency.value;
        processTab(current);
    }
});

watch(currency, async (current) => {
    if (tabs.length === 0) return;

    dialog.value = DialogUtility.alert({
        title: 'Running FX Conversion',
        content: 'Running FX Conversion on Cost Walk. Depending on the size of the selected Business Case this may take awhile. Please Wait',
        okButton: { cssClass: 'hidden' },
        showCloseIcon: false,
        closeOnEscape: false,
        animationSettings: { effect: 'Zoom' }
    });

    const tab = tabs[focusedTab.value];

    tab.Currency = current;
    tab.refreshUsedCurrencies();
    processTab(focusedTab.value);

    dialog.value.hide();
});

watch(tabs, () => {

    tabs.filter(model => model.isWorkerCompleted === true).forEach(tab => {
        tab.buildSheet(spreadsheet.value, false);

        tab.applyFormatting(spreadsheet.value);

        tab.isWorkerCompleted = false;
    });

    //dialog.value.hide();
}, { deep: true });

watch(availableParts, (current) => {
    if (current === undefined || current.find(q => q.id === undefined) !== undefined) return;
    partOptions.value = current.map(x => prepDataSelection(x));
}, { deep: true });


// H1: Methods
function onModalPickerClose() {
    let currentList = [];
    availableParts.value.forEach((x) => {
        let existing = partOptions.value.find(q => q.PartId == x.id);

        if (existing !== undefined) currentList.push(existing);
        else currentList.push(prepDataSelection(x));
    });

    let locBase = window.location.href.split("?")[0]
    let queryParams = currentList.map(x => `part=${encodeURI(atob(x.PartId))}`).join('&');
    window.history.pushState(null, "", `?${queryParams}`);

    partOptions.value = currentList;
}

function onPartChanged(partOp, year, slotIndex, tabIndex) {
    if (partOp === undefined || partOp === null) return;

    let tab = tabs[tabIndex];

    if (tab?.PartDataSets[slotIndex]?.PartId === partOp.PartId) return;

    if (year) partOp.DisplayYear = year;

    // Update slot context
    if (tab === undefined) {
        tab = new ComponentWalkModel();
        tab.TabIndex = tabIndex;

        tabs[tabIndex] = tab;
    }

    // Sets Currencies
    currencies.value = [...new Set(partOp.ExchangeRates.map(x => x.currency))]
        .map(x => {
            return { key: x, value: x };
        });

    tab.Currency = currency.value;
    tab.setSlot(slotIndex, partOp);
}

function onYearChanged(partId, year, slotIndex, tabIndex) {
    if (partId === undefined) return;

    const tab = tabs[tabIndex];

    if (tab?.PartDataSets === undefined || tab?.PartDataSets.length < 2) return;

    const partData = tab.PartDataSets[slotIndex]

    if (partData.PartId === partId && partData.DisplayYear === year) return;

    tab.PartDataSets[slotIndex].DisplayYear = year;

    processTab(tabIndex)

    spreadsheet.value.refresh();
}

/**
 * Constructs the ComponentWalkPartModel to be inserted into each tab's ComponentWalkModel slots
 * @param {object} partOption
 * @return {ComponentWalkPartModel}
 */
function prepDataSelection({ saleablePart, version, versionDetail, id, snapshot }) {
    let output = null;
    let exchRates = [];

    versionDetail.exchangeRates.forEach(rate => {
        rate.Item2.Values.forEach(item => {
            exchRates.push({
                currency: rate.Item1,
                year: new Date(item.Date).getUTCFullYear(),
                value: item.Value
            });
        });
    });

    output = new ComponentWalkPartModel(saleablePart, snapshot.data, exchRates);
    output.Version = version.id;
    output.VersionName = version.text;
    output.EcrNumber = versionDetail.id;
    output.PartId = id
    return output;
}

function onToggleHidden(args) {
    isExpanded.value = args.checked;

    const tab = tabs[focusedTab.value];
    if (args.checked) {
        tab.expand(spreadsheet.value);
    } else {
        tab.collapse(spreadsheet.value);
    }
    tab.applyFormatting(spreadsheet.value);
}

function save() {
    /*
        * https://www.syncfusion.com/kb/13200/how-to-remove-trial-version-tab-created-in-spreadsheet
        * https://ej2.syncfusion.com/javascript/documentation/spreadsheet/open-save/?_ga=2.55026156.612405243.1657025143-1803598672.1650372854
        * url: Specifies the save URL.
        * fileName: Specifies the file name.
        * saveType: Specifies the file type need to be saved. Xlsx, Xls, Csv and Pdf.
    */
    dialog.value = DialogUtility.alert({
        title: 'Exporting Component Walk',
        content: 'Exporting Component Walk to Excel. Depending on the size of the selected Business Case this may take awhile. Please Wait',
        okButton: { cssClass: 'hidden' },
        showCloseIcon: false,
        closeOnEscape: false,
        animationSettings: { effect: 'Zoom' }
    });

    const tmpSpreadsheet = new Spreadsheet();
    tmpSpreadsheet.appendTo("#exportpane");
    tmpSpreadsheet.scrollSettings.enableVirtualization = false;

    if (tabs.length > 1) tmpSpreadsheet.insertSheet(0, tabs.length - 2);

    tabs.sort((a, b) => b.TabIndex - a.TabIndex).forEach((tab, index) => {
        setTimeout(() => {

            tab.expand(tmpSpreadsheet, false);

            tab.buildSheet(tmpSpreadsheet, true);

            if (tmpSpreadsheet.activeSheetIndex != tab.TabIndex) tmpSpreadsheet.goTo(`${tab.TabName}!A1`);

            tab.applyFormatting(tmpSpreadsheet, true);

            tmpSpreadsheet.refresh();

        }, tabLoadingTimer.value * index);
    });
    setTimeout(() => { tmpSpreadsheet.save({ fileName: `Component Walk.xlsx`, url: "/export/excel", saveType: "Xlsx" }) }, (tabs.length + 1) * tabLoadingTimer.value);
    setTimeout(() => {
        exportpane.value.innerHtml = "";
        dialog.value.hide();
    }, (tabs.length + 2) * tabLoadingTimer.value);

}

function setSpreadsheetHeight() {
    height.value = document.documentElement.clientHeight - document.getElementById("spreadsheet").offsetTop - 1;
}

function cellEdit() {
    //JG: Used to cancel and not allow the edit as we don't want users making changes, but do want formulas to run.
    spreadsheet.value.closeEdit();
}

function onSelect(args) {
    selectedRange = args.range;
}

async function onContextMenuBeforeOpen(args) {
    args.cancel = true;

    const tab = tabs[focusedTab.value];
    let rowHasData = false;
    const slotrowrange = tab.getPartSlotRowRange(selectedRange);

    if (slotrowrange === undefined) return;

    const data = await spreadsheet.value.getData(slotrowrange.range);

    const ptnum = data.values().next().value?.value;
    rowHasData = ptnum !== '' && ptnum !== undefined;

    // no row captured and cell clicked within valid slot range and populated row
    if (rowPendingMove === null && rowHasData === true) {
        slotrowrange.partNumber = ptnum;
        // Highlight slot row
        spreadsheet.value.selectRange(slotrowrange.range);
        // Copy range reference data into pending
        rowPendingMove = slotrowrange;
        return;
    }

    // const isInSameSlot = 
    // a row captured and cell clicked within valid slot range and empty row range must also be same as origin
    if (rowPendingMove !== null && rowHasData === false && rowPendingMove.slot === slotrowrange.slot) {

        // Delete content from original range row
        await spreadsheet.value.cut(rowPendingMove.range);
        await spreadsheet.value.paste(slotrowrange.range, 'Values');
        tab.applySlotFormatting(spreadsheet.value, rowPendingMove.slot);

        // update PtIndex
        let originidx = tab.PtIndex.find(x => x.i === rowPendingMove.ptIndex).slots[rowPendingMove.slot];

        tab.PtIndex[slotrowrange.ptIndex].slots[slotrowrange.slot] = originidx;
        tab.PtIndex[rowPendingMove.ptIndex].slots[rowPendingMove.slot] = undefined;

        // reprocess 
        tab.reprocessDeltas(spreadsheet.value)
    }

    rowPendingMove = null;
}

function onActionBegin(args) {
    if (args.action === "gotoSheet") {
        const eventArgs = args.args.eventArgs;
        // Clear or replace header info with current context
        focusedTab.value = eventArgs.currentSheetIndex;
    }
}

function parsePartOptions() {
    availableParts.value = props.part.map((x, i) => {
        let partDetail = x.split("|");

        let output = {
            urlId: x,
            display: x.replaceAll("|", " - "),
            ecr: partDetail[0],
            version: partDetail[1],
            option: partDetail[2],
            part: partDetail[3],
            partSuffix: partDetail[4],
            partRev: partDetail[5],
            // data: {} 
        };

        return output;
    });
}

function processTab(tabIndex) {
    const tab = tabs[tabIndex];

    tab.Currency = currency.value

    spreadsheet.value.goTo(`[${tab.TabName}]!A1`);

    tab.buildSheet(spreadsheet.value, false);

    tab.applyFormatting(spreadsheet.value);

    spreadsheet.value.refresh();
}

async function launchPartPicker() {
    ecrPartPicker.value.openModal();
}

function createBookmark() {
    const tabNames = partOptions.value.map(x => x.getDisplayName()).join(" | ");
    Bookmark.CreateBookmark(window.history.state.current, `${ReportName.value} | ${tabNames}`, new Date());
    isBookmarked.value = true;
    emit(Bookmark.EventName);
}

function removeBookmark() {
    Bookmark.RemoveBookmark(route.fullPath);
    isBookmarked.value = false;
    emit(Bookmark.EventName);
}

</script>

<style>
.row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
}

.column {
    display: flex;
    flex-direction: column;
    flex-basis: 100%;
    flex: 1;
    align-items: center;
}

.align-end {
    align-items: flex-end;
}

.align-start {
    align-items: flex-start;
    justify-content: start;
}

p {
    margin: 2px 0;
}

.additional-controls {
    display: flex;
    justify-content: flex-end;
    color: white;
    font-size: 1rem;
    font-weight: 400;
    font-family: "Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif;
}

/* ATTN: adding sheet delete will cause issues with year, part, and currency dropdown changes */
/* .e-tab-wrap .e-close-icon { display: inline !important; } */
</style>